import { Injectable } from '@angular/core';
import { HostApiService } from 'lib-host-interface';
import { BehaviorSubject } from 'rxjs';
import { AssociationProfile, ModUserProfile } from 'projects/app-nmls-entity-profile/src/app/models/profiles.response';
import { USER_TYPES } from 'projects/app-nmls-entity-profile/src/app/models/profiles.enum';
import { AppHostUserInfo } from '../model/user.model';
import { environment } from '../../environments/environment';

export interface FilingStatusDisplayRules {
  filingStatus?: FILING_STATUS;
  canDelete?: boolean;
  canRecall?: boolean;
  canSubmit?: boolean;
  canRequestAttestation?: boolean;
  canAttest?: boolean;
  readOnlyMode?: boolean;
  displayedStatus?: string;
  isShowDisplayStatus?: boolean;
}

export interface CanAttestResponse {
  attestChecked: boolean;
  employmentHistoryReviewed?: boolean;
  hasError: boolean;
  isCompletenessCheckLoading: boolean;
  errorCount?: number;
}

export enum FILING_STATUS {
  UNSUBMITTED = 'Unsubmitted',
  ATTESTATION_REQUESTED = 'Attestation Requested',
  ATTESTED = 'Attested',
  SUBMITTED = 'Submitted',
  PROCESSED = 'Filing Processed',
  FAILED = 'Failed Processing',
}

interface AuthTarget {
  entityId: number;
  subjectId: number;
}

type CompareModToDecafAssociations = {
  [key in USER_TYPES]?: number[];
};

interface UserPermissions {
  isCompanyUser: boolean;
  isIndividualUser: boolean;
  isCreatedByEntity: boolean;
  isSubjectOfFiling: boolean;
  hasConfidentialInfoPermissions: boolean;
  hasAdministratorPermissions: boolean;
}

interface FilingStatusMatrix {
  [FILING_STATUS.UNSUBMITTED]: FilingStatusDisplayRules;
  [FILING_STATUS.ATTESTATION_REQUESTED]: FilingStatusDisplayRules;
  [FILING_STATUS.ATTESTED]: FilingStatusDisplayRules;
  [FILING_STATUS.SUBMITTED]: FilingStatusDisplayRules;
  [FILING_STATUS.PROCESSED]: FilingStatusDisplayRules;
  [FILING_STATUS.FAILED]: FilingStatusDisplayRules;
}

@Injectable({
  providedIn: 'root',
})
export class DisplayRulesService {
  hostApi: HostApiService;
  displayRulesSubj = new BehaviorSubject<FilingStatusDisplayRules>({});
  appHostUserInfoSub = new BehaviorSubject<AppHostUserInfo>({});
  displayRules$ = this.displayRulesSubj.asObservable();
  appHostUserInfo: AppHostUserInfo;
  associationIds: CompareModToDecafAssociations;
  // toggle flags for testing locally if needed
  userPermissions: UserPermissions = {
    isCompanyUser: false,
    isIndividualUser: false,
    isCreatedByEntity: false,
    isSubjectOfFiling: false,
    hasConfidentialInfoPermissions: false,
    hasAdministratorPermissions: false,
  };
  filingStatus: FilingStatusDisplayRules;
  canAttestResp: any = new BehaviorSubject<any>({
    attestChecked: false,
    hasError: false,
    isCompletenessCheckLoading: true,
  });
  canAttestResp$ = this.canAttestResp.asObservable();
  profileData: ModUserProfile;
  isRegulatorUser = new BehaviorSubject(false);
  navigationError = new BehaviorSubject(false);

  constructor() {}

  getCurrentDisplayRules(): FilingStatusDisplayRules {
    return this.displayRulesSubj.getValue();
  }

  sentIsVerifiedData(data: boolean) {
    this.canAttestResp.next({
      ...this.canAttestResp.getValue(),
      attestChecked: data,
    });
  }

  setUserPermissions(
    profile: ModUserProfile,
    userInfo: AppHostUserInfo,
    role: string,
    permissions: string[],
    metadata?,
  ) {
    this.userPermissions.isCompanyUser = role === 'Company';
    this.userPermissions.isIndividualUser = role === 'Individual';
    this.isRegulatorUser.next(role === 'Regulator');
    this.appHostUserInfo = userInfo;
    console.log('PERMISSIONS', permissions);
    if (permissions.includes('ConfidentialInfo')) {
      this.userPermissions.hasConfidentialInfoPermissions = true;
    } else if (permissions.includes('Administrator')) {
      this.userPermissions.hasAdministratorPermissions = true;
    }

    // index by userType and create list of entityIds
    this.profileData = profile;
    const associationIds = profile?.associations?.reduce((hashmap, { userType, entityId }: AssociationProfile) => {
      hashmap[userType] ? hashmap[userType].push(entityId) : (hashmap[userType] = [entityId]);
      return hashmap;
    }, {});
    console.log('setUserProfileMOD associationIds', associationIds);
    this.associationIds = associationIds;

    // filing status will be under attributes --> filingStatus -> current
    const filingStatus = metadata?.attributes?.filingStatus?.current;
    const authTarget = metadata?.target;
    this.setDisplayRulesByFilingStatus(filingStatus, authTarget, metadata);
  }

  setDisplayRulesByFilingStatus(filingStatus: FILING_STATUS, authTarget: AuthTarget, metadata?) {
    if (!authTarget) return;

    // entityId maps to company, subjectId maps to individual
    const { entityId, subjectId } = authTarget;

    this.userPermissions.isCreatedByEntity = this.associationIds?.Company?.some((id) => id === entityId);
    this.userPermissions.isSubjectOfFiling = this.associationIds?.Individual?.some((id) => id === subjectId);
    this.displayRulesSubj.next(this.getDisplayRulesByFilingStatus(filingStatus, this.userPermissions, metadata));
  }

  getDisplayRulesByFilingStatus(status: FILING_STATUS, userPermissions: UserPermissions, metadata?) {
    if (!userPermissions) return;

    const { isCompanyUser, isIndividualUser, isCreatedByEntity, isSubjectOfFiling } = userPermissions;
    const companyIsAuthorized = (isCompanyUser && isCreatedByEntity) || false;
    const individualIsAuthorized = (isIndividualUser && isSubjectOfFiling) || false;
    const { orgClass } = metadata?.userInfo?.createdBy ?? '';
    const { displayedStatus, isShowDisplayStatus } = this.getDisplayedStatus(
      orgClass,
      status,
      isIndividualUser,
      isCompanyUser,
    );
    const isNotCurrentVersion = !!(
      metadata?.templateVersion && metadata.templateVersion !== environment.currentVersion
    );

    const ruleMap: FilingStatusMatrix = {
      [FILING_STATUS.UNSUBMITTED]: {
        canDelete: companyIsAuthorized || individualIsAuthorized,
        canRequestAttestation: companyIsAuthorized,
        canSubmit: individualIsAuthorized,
        readOnlyMode: isNotCurrentVersion,
        isShowDisplayStatus: isShowDisplayStatus,
        displayedStatus: displayedStatus,
      },
      [FILING_STATUS.ATTESTATION_REQUESTED]: {
        canDelete: companyIsAuthorized,
        canAttest: individualIsAuthorized,
        canRecall: companyIsAuthorized,
        readOnlyMode: companyIsAuthorized || isNotCurrentVersion,
        isShowDisplayStatus: isShowDisplayStatus,
        displayedStatus: displayedStatus,
      },
      [FILING_STATUS.ATTESTED]: {
        filingStatus: FILING_STATUS.ATTESTED,
        canDelete: companyIsAuthorized,
        canRecall: companyIsAuthorized,
        canSubmit: companyIsAuthorized,
        readOnlyMode: companyIsAuthorized || individualIsAuthorized || isNotCurrentVersion,
        isShowDisplayStatus: isShowDisplayStatus,
        displayedStatus: displayedStatus,
      },
      [FILING_STATUS.SUBMITTED]: {
        isShowDisplayStatus: isShowDisplayStatus,
        readOnlyMode: companyIsAuthorized || individualIsAuthorized || isNotCurrentVersion,
        displayedStatus: displayedStatus,
      },
      [FILING_STATUS.PROCESSED]: {
        isShowDisplayStatus: isShowDisplayStatus,
        readOnlyMode: companyIsAuthorized || individualIsAuthorized || isNotCurrentVersion,
        displayedStatus: displayedStatus,
      },
      [FILING_STATUS.FAILED]: {
        isShowDisplayStatus: isShowDisplayStatus,
        readOnlyMode: companyIsAuthorized || individualIsAuthorized || isNotCurrentVersion,
        displayedStatus: displayedStatus,
      },
    };

    //  ruleMap[status] = {
    //    canDelete: true,
    //    canAttest: true,
    //    canSubmit: true,
    //  };

    console.log(ruleMap[status], 'RuleMap Status');
    this.filingStatus = ruleMap[status];

    return ruleMap[status];
  }

  getDisplayedStatus(orgClass, filingStatus, isIndividualUser, isCompanyUser) {
    if (filingStatus === FILING_STATUS.UNSUBMITTED && isIndividualUser && orgClass === 'Individual') {
      return { displayedStatus: 'Draft', isShowDisplayStatus: true };
    } else if (filingStatus === FILING_STATUS.UNSUBMITTED && isCompanyUser && orgClass === 'Company') {
      return { displayedStatus: 'Draft', isShowDisplayStatus: true };
    } else if (filingStatus === FILING_STATUS.ATTESTATION_REQUESTED && isIndividualUser && orgClass === 'Company') {
      return { displayedStatus: 'Attestation Requested', isShowDisplayStatus: true };
    } else if (filingStatus === FILING_STATUS.ATTESTATION_REQUESTED && isCompanyUser && orgClass === 'Company') {
      return { displayedStatus: 'Attestation Requested', isShowDisplayStatus: true };
    } else if (filingStatus === FILING_STATUS.ATTESTED && isIndividualUser && orgClass === 'Company') {
      return { displayedStatus: 'Attested', isShowDisplayStatus: true };
    } else if (filingStatus === FILING_STATUS.ATTESTED && isCompanyUser && orgClass === 'Company') {
      return { displayedStatus: 'Attested', isShowDisplayStatus: true };
    } else if (
      filingStatus === FILING_STATUS.SUBMITTED &&
      (isIndividualUser || isCompanyUser) &&
      (orgClass === 'Company' || orgClass === 'Individual')
    ) {
      return { displayedStatus: 'Submitted', isShowDisplayStatus: true };
    } else if (
      filingStatus === FILING_STATUS.PROCESSED &&
      (isIndividualUser || isCompanyUser) &&
      (orgClass === 'Company' || orgClass === 'Individual')
    ) {
      return { displayedStatus: 'Filing Processed', isShowDisplayStatus: true };
    } else if (
      filingStatus === FILING_STATUS.FAILED &&
      (isIndividualUser || isCompanyUser) &&
      (orgClass === 'Company' || orgClass === 'Individual')
    ) {
      return { displayedStatus: 'Failed Processing', isShowDisplayStatus: true };
    } else {
      return { displayedStatus: '', isShowDisplayStatus: false };
    }
  }
}
